import { createTheme } from '@mui/material/styles';
import type { PaletteMode, Theme } from '@mui/material/styles';

/**
 * Design-system tokens derived from the OTrack prototype.
 *
 * Colors, typography and spacing live here and nowhere else. Components reference
 * `theme.palette.*`, `theme.otrack.*` or the `sx` system — never a bare hex literal.
 * Dark mode is a full token swap; the sidebar is always dark regardless of mode.
 */

// ── Status tones ─────────────────────────────────────────────────────────────

export const statusPalette = {
  pending: { light: '#B5791A', dark: '#E0A94A' },
  published: { light: '#188A5A', dark: '#43C285' },
  dead: { light: '#C8362F', dark: '#F0776E' },
  info: { light: '#2A47C9', dark: '#6E86FF' },
  accent: { light: '#0B9489', dark: '#31D3C2' },
} as const;

export type StatusTone = keyof typeof statusPalette;

// ── Categorical accents ──────────────────────────────────────────────────────

/**
 * Identity colours for things shown side by side (KPI tiles, chart series). Kept apart from
 * the status tones above, which carry meaning (pending / published / dead) and must not be
 * reused for decoration. Each mode is its own validated step, not an automatic flip; the
 * order below is the one the adjacent-pair colour-blind check was run against.
 */
export const categoricalPalette = {
  blue: { light: '#2A78D6', dark: '#3987E5' },
  aqua: { light: '#1BAF7A', dark: '#199E70' },
  orange: { light: '#EB6834', dark: '#D95926' },
  violet: { light: '#4A3AA7', dark: '#9085E9' },
} as const;

export type CategoricalHue = keyof typeof categoricalPalette;

// ── Sidebar palette (always dark, mode-independent) ───────────────────────────

export const sidebar = {
  bg: '#0E1524',
  bg2: '#161F35',
  ink: '#EAEEF8',
  ink2: '#96A2BC',
  line: '#243049',
  activeGrad: 'linear-gradient(90deg,rgba(42,71,201,.28) 0%,rgba(15,181,166,.14) 100%)',
  accentBar: '#0FB5A6',
  hover: 'rgba(255,255,255,.05)',
} as const;

// ── Module augmentation ───────────────────────────────────────────────────────

declare module '@mui/material/styles' {
  interface Theme {
    status: Record<StatusTone, string>;
    otrack: {
      sidebar: typeof sidebar;
      monoFamily: string;
      displayFamily: string;
      categorical: Record<CategoricalHue, string>;
    };
  }
  interface ThemeOptions {
    status?: Record<StatusTone, string>;
    otrack?: {
      sidebar?: Partial<typeof sidebar>;
      monoFamily?: string;
      displayFamily?: string;
      categorical?: Record<CategoricalHue, string>;
    };
  }
}

// ── Font families ─────────────────────────────────────────────────────────────

export const displayFontFamily = '"Sora", system-ui, -apple-system, "Segoe UI", sans-serif';

export const bodyFontFamily =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const monoFontFamily =
  '"IBM Plex Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

// ── Theme factory ─────────────────────────────────────────────────────────────

export function buildTheme(mode: PaletteMode): Theme {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? '#6E86FF' : '#2A47C9',
        light: isDark ? '#8FA4FF' : '#4F6AE3',
        dark: isDark ? '#4F6AE3' : '#1B35A8',
      },
      secondary: {
        main: isDark ? '#31D3C2' : '#0FB5A6',
        light: isDark ? '#5EDDD0' : '#3DC8BB',
        dark: isDark ? '#1BA89A' : '#0A9488',
      },
      error: {
        main: isDark ? '#F0776E' : '#C8362F',
        light: isDark ? '#F4968F' : '#D85249',
        dark: isDark ? '#D45850' : '#A82920',
      },
      warning: {
        main: isDark ? '#E0A94A' : '#B5791A',
        light: isDark ? '#E8BC6E' : '#C98F2F',
        dark: isDark ? '#C08B30' : '#965F0E',
      },
      success: {
        main: isDark ? '#43C285' : '#188A5A',
        light: isDark ? '#68CF9E' : '#2DA66D',
        dark: isDark ? '#2A9E68' : '#106E45',
      },
      info: {
        main: isDark ? '#6E86FF' : '#2A47C9',
      },
      background: {
        default: isDark ? '#0A0F1C' : '#F5F7FB',
        paper: isDark ? '#111827' : '#FFFFFF',
      },
      divider: isDark ? '#222E45' : '#E6EAF2',
      text: {
        primary: isDark ? '#EAEEF8' : '#0E1524',
        secondary: isDark ? '#A9B4C8' : '#48546B',
        disabled: isDark ? '#6C7891' : '#8A94A6',
      },
      action: {
        hover: isDark ? 'rgba(255,255,255,.04)' : 'rgba(14,21,36,.03)',
        selected: isDark ? 'rgba(110,134,255,.12)' : 'rgba(42,71,201,.06)',
      },
    },

    status: Object.fromEntries(
      Object.entries(statusPalette).map(([key, val]) => [key, isDark ? val.dark : val.light]),
    ) as Record<StatusTone, string>,

    otrack: {
      sidebar,
      monoFamily: monoFontFamily,
      displayFamily: displayFontFamily,
      categorical: Object.fromEntries(
        Object.entries(categoricalPalette).map(([hue, tone]) => [
          hue,
          isDark ? tone.dark : tone.light,
        ]),
      ) as Record<CategoricalHue, string>,
    },

    typography: {
      fontFamily: bodyFontFamily,
      // Display headings use Sora for the brand feel.
      h1: {
        fontFamily: displayFontFamily,
        fontSize: '1.75rem',
        fontWeight: 600,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontFamily: displayFontFamily,
        fontSize: '1.375rem',
        fontWeight: 600,
        letterSpacing: '-0.015em',
      },
      h3: {
        fontFamily: displayFontFamily,
        fontSize: '1.125rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h4: { fontFamily: displayFontFamily, fontSize: '1rem', fontWeight: 600 },
      h5: { fontFamily: displayFontFamily, fontSize: '0.9375rem', fontWeight: 600 },
      h6: { fontFamily: displayFontFamily, fontSize: '0.875rem', fontWeight: 600 },
      subtitle1: { fontSize: '0.875rem', fontWeight: 600 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 600 },
      body1: { fontSize: '0.875rem' },
      body2: { fontSize: '0.8125rem' },
      caption: { fontSize: '0.75rem' },
      overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.07em', lineHeight: 2 },
      button: { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
    },

    shape: { borderRadius: 8 },

    shadows: [
      'none',
      '0 1px 2px rgba(16,24,40,.05)',
      '0 1px 3px rgba(16,24,40,.1),0 1px 2px rgba(16,24,40,.06)',
      '0 4px 8px -2px rgba(16,24,40,.1),0 2px 4px -2px rgba(16,24,40,.06)',
      '0 8px 24px -12px rgba(16,24,40,.14),0 1px 2px rgba(16,24,40,.05)',
      '0 12px 32px -14px rgba(16,24,40,.2)',
      '0 16px 40px -16px rgba(16,24,40,.24)',
      '0 20px 48px -18px rgba(16,24,40,.28)',
      '0 24px 60px -20px rgba(16,24,40,.35)',
      '0 28px 64px -22px rgba(16,24,40,.38)',
      '0 30px 70px -20px rgba(16,24,40,.45)',
      '0 32px 72px -22px rgba(16,24,40,.48)',
      '0 36px 80px -24px rgba(16,24,40,.52)',
      '0 40px 90px -26px rgba(16,24,40,.56)',
      '0 44px 96px -28px rgba(16,24,40,.60)',
      '0 48px 100px -30px rgba(16,24,40,.64)',
      '0 52px 108px -32px rgba(16,24,40,.68)',
      '0 56px 112px -34px rgba(16,24,40,.72)',
      '0 60px 120px -36px rgba(16,24,40,.76)',
      '0 64px 128px -38px rgba(16,24,40,.80)',
      '0 68px 132px -40px rgba(16,24,40,.84)',
      '0 72px 140px -42px rgba(16,24,40,.88)',
      '0 76px 148px -44px rgba(16,24,40,.90)',
      '0 80px 156px -46px rgba(16,24,40,.92)',
      '0 84px 160px -48px rgba(16,24,40,.94)',
    ] as Theme['shadows'],

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarGutter: 'stable',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingLeft: 16, paddingRight: 16 },
          sizeSmall: { fontSize: '0.8125rem', padding: '4px 12px' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 99, fontWeight: 600, fontSize: '0.75rem' },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 12,
            boxShadow: theme.shadows[1],
          }),
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: { '& .MuiOutlinedInput-root': { borderRadius: 8 } },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiTableCell-root': {
              backgroundColor: isDark ? '#0E1522' : '#FBFCFE',
              fontWeight: 600,
              fontSize: '0.6875rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: theme.palette.text.disabled,
            },
          }),
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            cursor: 'pointer',
            '&:hover': { backgroundColor: theme.palette.action.hover },
            '&:last-child td': { borderBottom: 'none' },
          }),
        },
      },
    },
  });
}
